import { Userpilot } from 'userpilot';
import { isUserOnboarding } from '../../components/gamificationTooltip/helpers';
import {
  CURATOR_OPEN_SOURCE,
  IS_SHARED,
  OPEN_IMAGES,
  USERPILOT_UUID,
} from '../../constants/constants';
import { getCurrentUser } from '../../contexts/UserContext';
import {
  selectUserpilotOnboardingInfo,
  selectUserpilotSavedSteps,
  selectUserpilotState,
} from '../../redux/slicers/activitySlicer';
import { getAppState } from '../../redux/store';
import { GOOGLE_SIGN_UP, LF_DEBUGGER_EVENT_TRACKING } from '../../config/constant/projectConstant';
import { isCuratorPage } from '../../helpers/urlHelper';
import { selectCreatedPreviewList } from '../../redux/slicers/admin/paginatedRenderPreviewsSlicer';
import { getUserpilotEventKeysForCurator, isProductionUrl, shouldTrackUser } from '../Mixpanel';
import { selectSelectedObjectList } from '../../redux/slicers/admin/curatorUnityObjectSlicer';
import { sendUnityEvent } from '../../components/container/unityContainer';
import { LogConsole } from '../../helpers/logEvents';
import { getLocalStorage, getUserTimezone } from '../../helpers/jsHelper';
import { getCurrentDeviceInfo } from '../../helpers/segmentHelper';
import { EG_SUBMISSION } from '../../constants/projectConstants';

export const USERPILOT_PRODUCTION_TOKEN = 'NX-7dee3a76';
export const USERPILOT_STAGING_TOKEN = 'STG-NX-7dee3a76';

// this function we are only using in the useUserPilot hook, for mixpanel we have created a copy of this function.
// Note: If you add new keys here, also update it in the getBasicUserDetails in the Mixpanel.js
export const getBasicUserDetails = () => {
  const currentUser = getCurrentUser();
  const searchParams = new URLSearchParams(window.location.search);
  const isOnboardingState = isUserOnboarding();

  const deviceInfo = Object.entries(getCurrentDeviceInfo()).reduce((acc, [key, value]) => {
    acc[key.toLowerCase()] = value;
    return acc;
  }, {});

  return {
    // user_id: currentUser?.user_pilot_id || localStorage.getItem(USERPILOT_UUID),
    imagine_id: currentUser?.user?.id,
    member_id: currentUser?.user?.member_id,
    email: currentUser?.user?.email || getLocalStorage(EG_SUBMISSION),
    created_at: currentUser?.user?.userRegisteredOn,
    is_guest_user: currentUser?.is_guest ? 'Yes' : 'No',
    page_url: window.location.href,
    page_title: document.title,
    is_remove_watermark: searchParams?.get(OPEN_IMAGES) === 'true' ? 'Yes' : 'No',
    is_shared_experience: searchParams?.get(IS_SHARED) ? 'Yes' : 'No',
    curator_open_source: searchParams?.get(CURATOR_OPEN_SOURCE), // 'Catalog, Model upload, Texture upload, Templates, Example edit',
    is_gamified_onboarding: isOnboardingState ? 'Yes' : 'No',
    is_website: false,
    is_google_signup:
      getLocalStorage(GOOGLE_SIGN_UP) === true || getLocalStorage(GOOGLE_SIGN_UP) === 'true'
        ? 'Yes'
        : 'No',
    time_stamp: Math.floor(Date.now() / 1000),
    page_source: document.title,
    utm_source: searchParams?.get('utm_source'),
    utm_campaign: searchParams?.get('utm_campaign'),
    utm_medium: searchParams?.get('utm_medium'),
    utm_content: searchParams?.get('utm_content'),
    utm_term: searchParams?.get('utm_term'),
    is_test_user: localStorage.getItem(LF_DEBUGGER_EVENT_TRACKING) === 'true' ? 'Yes' : 'No',
    full_name: currentUser?.user?.name,
    company: currentUser?.user?.company_name,
    current_org_id: currentUser?.organization_detail?.id,
    current_org_name: currentUser?.organization_detail?.name,
    current_org_subscription_type: currentUser?.organization_detail?.subscription_type,
    current_org_subscription_status_id: currentUser?.organization_detail?.status?.id,
    current_org_subscription_status_name: currentUser?.organization_detail?.status?.name,
    was_guest_user: currentUser?.was_guest_user ? 'Yes' : 'No',

    ...getUserpilotEventKeysForCurator(),
    ...(isCuratorPage()
      ? {
          render_name: selectCreatedPreviewList(getAppState())?.name,
        }
      : {}),

    ...(deviceInfo || {}),

    ip_location: '',

    timezone: getUserTimezone(),
  };
};

export const getUserpilotStepState = async () => {
  return new Promise((resolve) => {
    const steps = selectUserpilotSavedSteps(getAppState());
    resolve(steps ? steps : {});
  });
};

export const destroyUserpilotOnboarding = () => {
  const userpilotInfo = selectUserpilotOnboardingInfo(getAppState());
  if (userpilotInfo?.isRunning) {
    Userpilot.end(userpilotInfo?.token);
  }
};

export const userPilotEvents = () => {
  const isActive = () => selectUserpilotOnboardingInfo(getAppState());

  const trackUserPilotEvent = (eventName = '', properties = {}) => {
    if (eventName && shouldTrackUser()) { // removed '!isProductionUrl'
      LogConsole('DEBUG__trackUserPilotEvent: ', {
        eventName,
        properties: {
          ...getBasicUserDetails(),
          ...properties,
        },
        isProduction: isProductionUrl ? 'Yes' : 'No',
      });
      Userpilot.track(eventName, {
        ...getBasicUserDetails(),
        ...properties,
      });
    }
  };

  const trackPageViewEvent = (properties = {}) => {
    trackUserPilotEvent('Page View', {
      page_url: location.href,
      page_name: document.title,
      ...properties,
    });
  };

  const trackPageVisitedEvent = (pageName, properties = {}) => {
    trackUserPilotEvent('Visited - ' + pageName, {
      page_url: location.href,
      ...properties,
    });
  };

  const trackRoomLoadedEvent = (properties = {}) => trackUserPilotEvent('Room Loaded', properties);

  const trackFocusObjectEvent = (properties = {}) => {
    const objectList = selectSelectedObjectList(getAppState());
    objectList?.forEach((a) => {
      const object = {
        id: a?.id,
        name: a?.name,
        objectName: a?.objectName,
        ...properties,
      };

      if (object) trackUserPilotEvent('Focus Object', object);
    });
  };

  const trackCameraRotateEvent = (properties = {}) =>
    trackUserPilotEvent('Camera Rotate', properties);

  const trackCameraPanEvent = (properties = {}) => trackUserPilotEvent('Camera Pan', properties);

  const trackCameraZoomInOutEvent = (properties = {}) =>
    trackUserPilotEvent('Camera Zoom In/Out', properties);

  const trackCuratorLeftNavClickedEvent = (properties = {}) =>
    trackUserPilotEvent('Curator Left Nav Clicked', properties);

  const trackRenderSuccessful = (properties = {}) =>
    trackUserPilotEvent('Render Successful', properties);

  const trackCuratorSaveButtonClickedEvent = (properties = {}) =>
    trackUserPilotEvent('Save Button Click', properties);

  const trackOnboardingStarted = (properties = {}) =>
    trackUserPilotEvent('Onboarding Started', properties);

  const trackOnboardingFinished = (properties = {}) =>
    trackUserPilotEvent('Onboarding Finished', properties);

  const trackOnboardingDropped = (properties = {}) =>
    trackUserPilotEvent('Onboarding Dropped', properties);

  const trackCreateImageClicked = (properties = {}) =>
    trackUserPilotEvent('Create Image Clicked', properties);

  const trackCreateImageSubmit = (properties = {}) =>
    trackUserPilotEvent('Create Image Submit', properties);

  const trackCreateImagePopupClosed = (properties = {}) =>
    trackUserPilotEvent('Create Image Popup Closed', properties);

  const trackHighResolutionOptionClicked = (properties = {}) =>
    trackUserPilotEvent('High Resolution Option Clicked', properties);

  const trackDownloadImageClicked = (properties = {}) =>
    trackUserPilotEvent('Download Image Clicked', properties);

  const trackBuyImageClicked = (properties = {}) =>
    trackUserPilotEvent('Buy Image Clicked', properties);

  const trackDownloadCurrentImageSelected = (properties = {}) =>
    trackUserPilotEvent('Download Current Image Selected', properties);

  const trackDownloadImageSuccess = (properties = {}) =>
    trackUserPilotEvent('Download Image Success', properties);

  const trackDownloadIncludePassesSelected = (properties = {}) =>
    trackUserPilotEvent('Download Include Passes Selected', properties);

  const trackImageVariationSelected = (properties = {}) =>
    trackUserPilotEvent('Image Variation Selected', properties);

  const trackRenderPreviewDialogClosed = (properties = {}) =>
    trackUserPilotEvent('Close Image Panel', properties);

  const trackSignUpPopupOpened = (properties = {}) => {
    trackUserPilotEvent('Sign up popup opened', {
      ...properties,
    });
  };

  const trackClickEvent = (eventName, properties = {}) => {
    trackUserPilotEvent('Clicked - ' + eventName, {
      ...properties,
    });
  };

  const trackSignupExistingOrgPopup = (eventName, properties = {}) => {
    trackUserPilotEvent('Signup Existing Org Popup', {
      ...properties,
    });
  };

  const trackSignupSignupNewOrg = (eventName, properties = {}) => {
    trackUserPilotEvent('Signup Create New Org', {
      ...properties,
    });
  };

  const trackSignupCreateNewOrgSuccess = (eventName, properties = {}) => {
    trackUserPilotEvent('Signup Create New Org Success', {
      ...properties,
    });
  };

  const trackClickedJoinExistingRrg = (eventName, properties = {}) => {
    trackUserPilotEvent('Clicked join existing org', {
      ...properties,
    });
  };

  const trackRequestSent = (eventName, properties = {}) => {
    trackUserPilotEvent('Request Sent', {
      ...properties,
    });
  };

  const trackDeniedRequest = (eventName, properties = {}) => {
    trackUserPilotEvent('Denied Request', {
      ...properties,
    });
  };

  const trackAcceptRequest = (eventName, properties = {}) => {
    trackUserPilotEvent('Accept Request', {
      ...properties,
    });
  };

  const trackResendRequest = (eventName, properties = {}) => {
    trackUserPilotEvent('Resend Request', {
      ...properties,
    });
  };

  const goToNextStep = () => {
    const { isPropHighlighted, isGizmoHighlighted } = selectUserpilotState(getAppState());

    if (isActive()) Userpilot.next();
    if (isPropHighlighted) sendUnityEvent('OnBoardingRoomModule', 'StopBlinkingReceiver');
    if (isGizmoHighlighted) sendUnityEvent('OnBoardingRoomModule', 'StopGizmoBlinking');
  };

  const trackEditImageApplyConfiguration = (properties = {}) => {
    trackUserPilotEvent('Image Editor', {
      ...properties,
    });
  };

  return {
    trackUserPilotEvent,
    trackPageVisitedEvent,
    trackPageViewEvent,
    trackRoomLoadedEvent,
    trackFocusObjectEvent,
    trackCameraRotateEvent,
    trackCameraPanEvent,
    trackCameraZoomInOutEvent,
    trackCuratorLeftNavClickedEvent,
    trackCuratorSaveButtonClickedEvent,
    trackRenderSuccessful,
    trackOnboardingStarted,
    trackOnboardingFinished,
    trackOnboardingDropped,
    trackCreateImageClicked,
    trackCreateImageSubmit,
    trackCreateImagePopupClosed,
    trackHighResolutionOptionClicked,
    trackDownloadImageClicked,
    trackBuyImageClicked,
    trackDownloadCurrentImageSelected,
    trackDownloadImageSuccess,
    trackDownloadIncludePassesSelected,
    trackImageVariationSelected,
    trackSignUpPopupOpened,
    goToNextStep,
    trackClickEvent,
    trackRenderPreviewDialogClosed,
    trackSignupExistingOrgPopup,
    trackSignupSignupNewOrg,
    trackSignupCreateNewOrgSuccess,
    trackClickedJoinExistingRrg,
    trackRequestSent,
    trackDeniedRequest,
    trackAcceptRequest,
    trackResendRequest,
    trackEditImageApplyConfiguration,
  };
};
